import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import { exportView, NavUtils } from "../../navigation";
import { useApi } from "../../common/hooks";
import { CustomizableProjectPagesService } from "../../api";
import { CustomizableForm } from "../../common/components/customizable-form";
import { CustomButton, CustomDialog, ViewBanner } from "../../common/components";
import { hasPermission, Permissions, translate } from "../../common/providers";
import { areAllIdsInT2, getIsMandatoryField } from "./utils";
import PERMISSIONS, { SCOPES } from "../../common/providers/permissions/permissions";
import LoadingOverlay from "../../common/components/loaders/LoadingOverlay";
import style from "./NewPortfolio.module.css";

const NewPortfolio = () => {
	const [isCompleted, setIsCompleted] = useState(false);
	const [customPages, setCustomPages] = useState();
	const [projectName, setProjectName] = useState("");
	const [nameCustomizablePage, setNameCustomizablePage] = useState("");
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	const [updatePayload, setUpdatePayload] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const { call: getAllCustomPagesToCreate } = useApi(CustomizableProjectPagesService.getAllCustomPagesToCreate);
	const { call: createCustomPage } = useApi(CustomizableProjectPagesService.createCustomPage);
	const { call: getNameCustomizablePage } = useApi(CustomizableProjectPagesService.getNameCustomizablePage);
	const roles = useSelector(({ context }) => context.roles);
	const navigate = useNavigate();
	const params = useParams();
	const hasEditorRights = useMemo(
		() =>
			hasPermission([Permissions.COMPANY_ADMIN]) ||
			roles[SCOPES.COMPANY].some((role) => role === PERMISSIONS.COMPANY_ADMIN.role),
		[roles]
	);
	const getSections = (portfolioId) => {
		getAllCustomPagesToCreate({ portfolioId })
			.then((custom) => {
				const newJ = JSON.parse(custom?.pageJson);
				setCustomPages(newJ);
			})
			.catch(console.error);
	};
	useEffect(() => {
		getNameCustomizablePage({ portfolioId: params.idPortfolio })
			.then((data) => {
				setNameCustomizablePage(data.name);
				getSections(data.id);
			})
			.catch(console.error);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const testMandatory = () => {
		// eslint-disable-next-line
		const isMandatoryList = getIsMandatoryField(customPages?.sections || []);
		return areAllIdsInT2(isMandatoryList, updatePayload);
	};
	const handleGlobalUpdate = ({ uuid, value }) => {
		if (uuid === "f7a4e2f7-e60e-4293-8dc0-69bc5ea90c95" || uuid === "50dec57f-fc41-45bb-aaea-5341c5f536c4") {
			setProjectName(value);
		}
		if (!updatePayload.some((block) => block.uuid === uuid)) {
			setUpdatePayload((prev) => [...prev, { uuid, value }]);
		} else {
			setUpdatePayload((prev) => prev.map((block) => (block.uuid === uuid && { uuid, value }) || block));
		}
	};
	const onSubmit = () => {
		const customPageId = params.idPortfolio;
		const fields = updatePayload
			?.filter((item) => item.value)
			?.map((item) => ({
				fieldUuid: item.uuid,
				values: typeof item.value === "string" ? [item.value] : item?.value?.toBeAdded,
			}));
		const payload = { name: projectName, fields };
		if (testMandatory() && projectName !== "") {
			setIsLoading(true);
			createCustomPage({ customPageId }, payload)
				.then(({ id }) => {
					navigate((id && NavUtils.goToDocumentCenter(id)) || NavUtils.goToHome());
				})
				.catch(console.error)
				.finally(() => setIsLoading(false));
		}
	};
	useEffect(() => {
		setIsCompleted(testMandatory() && projectName !== "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectName, updatePayload]);
	const handleOpenCancelDialog = () => {
		setOpenCancelDialog((prev) => !prev);
	};
	const handleBackHome = () => navigate(NavUtils.goToHome());
	return (
		<div className={style.container}>
			{isLoading && <LoadingOverlay />}
			<ViewBanner titles={[{ title: translate(`new-portfolio.title.${nameCustomizablePage?.toLowerCase()}`) }]} />
			<div className={style.containerForm}>
				<CustomizableForm
					customPageId={params.idPortfolio}
					editMode={false}
					hasEditorRights={hasEditorRights}
					sections={customPages}
					onGlobalUpdate={handleGlobalUpdate}
				/>
			</div>
			<Grid container className={style.containerAction} direction="row" justifyContent="space-between">
				<CustomButton variant="outlined" onClick={handleOpenCancelDialog}>
					{translate("new-portfolio.cancel.button")}
				</CustomButton>
				<CustomButton disabled={!isCompleted} variant="contained" onClick={onSubmit}>
					{translate("common:btn.add")}
				</CustomButton>
			</Grid>
			<CustomDialog
				open={openCancelDialog}
				submitLabel={translate("common:btn.confirm")}
				subTitle={translate("new-portfolio.cancel.warning")}
				onClose={handleOpenCancelDialog}
				onSubmit={handleBackHome}
			/>
		</div>
	);
};

export default exportView({
	path: "/new-portfolio/:idPortfolio",
	localesPath: "/new-portfolio/locales",
	component: NewPortfolio,
});
